import gql from "graphql-tag";
import debounce from "lodash.debounce";
import {useMemo, useState} from "react";

import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {getContentSearchResults_searchPage_results as SearchResult} from "./__graphql-types__/getContentSearchResults.ts";
import type {getContentSearchResultsType} from "./__graphql-types__/index.ts";

export const CONTENT_SEARCH_QUERY: GqlOperation<
    getContentSearchResultsType["response"],
    getContentSearchResultsType["variables"]
> = gqlOp(gql`
    query getContentSearchResults(
        $query: String!
        $allowedTopicAncestors: [String]
        $contentKinds: [String]
        $numResults: Int
    ) {
        searchPage(
            query: $query
            allowedTopicAncestors: $allowedTopicAncestors
            contentKinds: $contentKinds
            numResults: $numResults
        ) {
            results {
                contentId
                kind
                learnableContent {
                    id
                    translatedTitle
                    contentDescriptor
                    translatedDescription
                    mappedStandards {
                        id
                        setId
                        standardId
                    }
                    parentTopic {
                        id
                        translatedTitle
                        contentKind
                        parent {
                            id
                            translatedTitle
                            contentKind
                            parent {
                                id
                                translatedTitle
                                contentKind
                            }
                        }
                    }
                }
            }
        }
    }
`);

export default function useContentSearch(options: {
    allowedTopicAncestors: string[];
    contentKinds: string[];
    numResults: number;
}) {
    // NOTE(reid): We keep track of exactly what is typed in (`typedQuery`)
    // and also a slightly delayed, debounced version (`delayedQuery`). This
    // lets us only send a new API request to the backend whenever the
    // `delayedQuery` gets updated - after the user has stopped typing for a
    // moment - rather than on every keypress.
    const [typedQuery, setTypedQuery] = useState("");
    const [delayedQuery, setDelayedQuery] = useState("");
    const setQueryDebounced = useMemo(() => debounce(setDelayedQuery, 500), []);

    const [result] = useQuery(CONTENT_SEARCH_QUERY, {
        variables: {
            query: delayedQuery,
            allowedTopicAncestors: options.allowedTopicAncestors,
            contentKinds: options.contentKinds,
            numResults: options.numResults,
        },
        fetchPolicy: FetchPolicy.CacheBeforeNetwork,
        skip: delayedQuery.length < 3,
    });

    const loading = result.status === "loading";

    const searchResults: SearchResult[] = [];
    if (result.status === "success") {
        for (const entry of result.data.searchPage.results || []) {
            if (!entry) {
                continue;
            }

            searchResults.push(entry);
        }
    }

    return {
        query: typedQuery,
        setQuery: (q) => {
            setTypedQuery(q);
            setQueryDebounced(q);
        },
        loading,
        searchResults,
    };
}
